import type { AxiosResponse } from 'axios';
import type { HttpClient } from 'services/api-client/client';
import { returnData } from 'services/api-client/client';
import type {
  CloudQueryConnectStateRead,
  CloudQueryIntegrations,
  CloudQueryIntegrationUpsertRequest,
  CloudQueryIntegrationUpsertResponse,
  CloudQuerySetupLoginResponse,
  CloudQuerySetupState,
  CloudQueryTenantStatus
} from 'types/api.types';

type ConnectBody = { organizationId: string };
type SetupBody = { organizationId: string; termsAccepted: true };

export default (client: HttpClient) => ({
  /* Read in the background wherever insights can appear - the projects list, the environments list and the
     environment page - and its consumers already render their own not-connected state. A 402 here must not
     throw the global pay-gate modal over whatever page the user is on. */
  getCloudQueryIntegration: (organizationId: string) =>
    client
      .get<CloudQueryIntegrations>(`cloudquery-integrations`, { params: { organizationId }, hidePayGateModal: true })
      .then(returnData),

  updateCloudQueryIntegration: (payload: CloudQueryIntegrationUpsertRequest) =>
    client.put<
      CloudQueryIntegrationUpsertResponse,
      AxiosResponse<CloudQueryIntegrationUpsertResponse>,
      CloudQueryIntegrationUpsertRequest
    >(`cloudquery-integrations`, payload),

  deleteCloudQueryIntegration: (id: string) => client.delete(`cloudquery-integrations/${id}`),

  getCloudQueryConnectState: (organizationId: string) =>
    client.get<CloudQueryConnectStateRead>(`cloudquery/connect`, { params: { organizationId } }).then(returnData),

  /* The in-env0 setup flow (ATL-468): env0 creates the CloudQuery tenant server to server. The terms
     are always sent as accepted because the confirm modal cannot submit without the checkbox. */
  setupCloudQuery: (organizationId: string) =>
    client
      .post<CloudQuerySetupState, AxiosResponse<CloudQuerySetupState>, SetupBody>(`cloudquery/setup`, {
        organizationId,
        termsAccepted: true
      })
      .then(returnData),

  getCloudQuerySetupStatus: (organizationId: string) =>
    client.get<CloudQuerySetupState>(`cloudquery/setup/status`, { params: { organizationId } }).then(returnData),

  getCloudQueryTenantStatus: (organizationId: string) =>
    client.get<CloudQueryTenantStatus>(`cloudquery/tenant/status`, { params: { organizationId } }).then(returnData),

  /* Mints a single-use same-tab login into the tenant; only the user who started the setup may call it. */
  /* The card renders the 409s (`not_ready`, `login_unavailable`) itself, so the global toast stays off. */
  setupCloudQueryLogin: (organizationId: string) =>
    client
      .post<CloudQuerySetupLoginResponse, AxiosResponse<CloudQuerySetupLoginResponse>, ConnectBody>(
        `cloudquery/setup/login`,
        { organizationId },
        { hideNotification: true }
      )
      .then(returnData)
});
